import { createSelector } from '@reduxjs/toolkit';
import { type FeatureSelectorWithFallback } from '@store/storeTypes';
import { type RootState } from '@store/types';

import type { LauncherPosition } from '../types';

interface FrontChatState {
  isInitialized: boolean;
  launcherVisible: boolean;
  windowOpen: boolean;
  unreadCount: number;
  initializationError: string | null;
  initializationStatus: 'idle' | 'initializing' | 'ready' | 'error';
  launcherPosition: LauncherPosition;
}

// Empty state fallback for when feature is not loaded
const emptyState: FrontChatState = {
  isInitialized: false,
  launcherVisible: true,
  windowOpen: false,
  unreadCount: 0,
  initializationError: null,
  initializationStatus: 'idle',
  launcherPosition: {
    side: 'left',
    bottom: 3,
  },
};

// Base selector with fallback
export const selectFrontChatState: FeatureSelectorWithFallback<
  FrontChatState,
  typeof emptyState
> = (state: RootState) => {
  // Type assertion needed for dynamic feature state
  const dynamicState = state as RootState & { frontChat?: FrontChatState };
  return dynamicState.frontChat || emptyState;
};

export const selectFrontChatIsInitialized = createSelector(
  [selectFrontChatState],
  state => state.isInitialized,
);

export const selectFrontChatLauncherVisible = createSelector(
  [selectFrontChatState],
  state => state.launcherVisible,
);

export const selectFrontChatWindowOpen = createSelector(
  [selectFrontChatState],
  state => state.windowOpen,
);

export const selectFrontChatUnreadCount = createSelector(
  [selectFrontChatState],
  state => state.unreadCount,
);

export const selectFrontChatLauncherPosition = createSelector(
  [selectFrontChatState],
  state => state.launcherPosition,
);
